/**
 * Onboarding checklist that guides farmers through farm setup.
 *
 * Two presentations, both rendering the same compact step list:
 * - "sidebar": docked in the desktop navigation sidebar, above the profile.
 * - "floating" (mobile): a small progress pill that expands into a bottom sheet.
 */

import CelebrationIcon from "@mui/icons-material/Celebration";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  LinearProgress,
  Link as MuiLink,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import confetti from "canvas-confetti";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  getAllArableFieldGeomsFromFields,
  getAllFieldGeomsFromFields,
  useArableFields,
  useCurrentFarm,
  useCurrentFarmAnimalGroups,
  useFields,
} from "model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUIStore } from "stores";
import { ONBOARDING_STEPS, OnboardingStep } from "./onboardingSteps";
import { useOnboardingState } from "./useOnboardingState";

/**
 * Routes where the onboarding checklist should be hidden on mobile.
 * These are typically routes where the user is actively completing onboarding steps.
 */
const HIDE_ON_MOBILE_ROUTES = [
  "/add-farm",
  "/add-field",
  "/add-animal-group",
  "/animal-group/", // When viewing animal group (for assign field)
];

interface OnboardingChecklistProps {
  /**
   * Where the checklist is rendered.
   * - "floating": mobile — a compact progress pill that expands into a bottom sheet.
   * - "sidebar": inline inside the desktop navigation sidebar, above the profile.
   */
  variant?: "floating" | "sidebar";
}

/**
 * Main onboarding checklist component.
 * Renders the desktop sidebar list or the mobile pill + bottom sheet.
 */
export function OnboardingChecklist({
  variant = "floating",
}: OnboardingChecklistProps = {}) {
  const isSidebar = variant === "sidebar";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isComplete,
    isDismissed,
    totalSteps,
    completedSteps,
    steps,
    isLoading,
  } = useOnboardingState();

  // Current farm — needed to route back to the farm tab on mobile
  const { data: currentFarm } = useCurrentFarm();
  const currentFarmId = currentFarm?.guid;

  // Get animal groups for dynamic route resolution
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();

  // Get fields to determine if we should show SBI import modal
  const { data: fields = [] } = useFields();
  const { data: arableFields = [] } = useArableFields();
  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(fields),
    [fields]
  );
  const arableFieldGeoms = useMemo(
    () => getAllArableFieldGeomsFromFields(arableFields),
    [arableFields]
  );
  const totalFieldCount =
    fieldGeoms.features.length + arableFieldGeoms.features.length;

  // Get SBI modal control from UI store
  const openSbiImportModal = useUIStore((state) => state.openSbiImportModal);

  // Get onboarding completion dialog state from UI store
  // This is triggered by AssignFieldDialog when the first field is assigned
  const showCompletionDialog = useUIStore(
    (state) => state.onboardingCompletionDialogOpen
  );
  const closeCompletionDialog = useUIStore(
    (state) => state.closeOnboardingCompletionDialog
  );

  // On mobile, hide checklist when user is on an onboarding-related route
  const shouldHideOnMobile =
    isMobile &&
    HIDE_ON_MOBILE_ROUTES.some((route) => location.pathname.startsWith(route));

  const handleAction = useCallback(
    (step: OnboardingStep) => {
      analytics.track(AnalyticsEvents.ONBOARDING_STEP_CLICKED, {
        step: step.id,
      });

      // For "add-field" step, show SBI import modal when there are < 5 fields
      if (step.id === "add-field" && totalFieldCount < 5) {
        // The SBI import modal is only mounted within the farm tab content, so
        // on mobile we must route to the farm tab first or it won't be visible.
        if (isMobile && currentFarmId) {
          navigate(`/farm/${currentFarmId}`);
        }
        openSbiImportModal();
        return;
      }

      let route = step.actionRoute;
      // Replace dynamic :animalGroupId with actual first animal group ID
      if (route.includes(":animalGroupId") && animalGroups.length > 0) {
        route = route.replace(":animalGroupId", animalGroups[0].guid);
      }
      navigate(route);
    },
    [
      navigate,
      animalGroups,
      totalFieldCount,
      openSbiImportModal,
      isMobile,
      currentFarmId,
    ]
  );

  // Don't show checklist if dismissed, complete, loading, or hidden on mobile.
  // But still render completion dialog if it's open.
  if (isDismissed || isComplete || isLoading || shouldHideOnMobile) {
    return (
      <CompletionDialog
        open={showCompletionDialog}
        onClose={closeCompletionDialog}
      />
    );
  }

  const progress = (completedSteps / totalSteps) * 100;

  // Desktop: compact list docked in the sidebar.
  if (isSidebar) {
    return (
      <SidebarChecklist
        steps={steps}
        completedSteps={completedSteps}
        totalSteps={totalSteps}
        progress={progress}
        onAction={handleAction}
      />
    );
  }

  // Mobile: a small progress pill that expands into a bottom sheet.
  return (
    <MobileOnboarding
      steps={steps}
      completedSteps={completedSteps}
      totalSteps={totalSteps}
      progress={progress}
      onAction={handleAction}
    />
  );
}

// ============ SHARED COMPACT LIST ============

const SIDEBAR_PURPLE = {
  text: "#7D5DA3",
  textStrong: "#433765",
  activeBg: "#e0daee",
  activeBgHover: "#d3c9e7",
  track: "#ded6ef",
  muted: "#a594c2",
};

interface SidebarChecklistProps {
  steps: ReturnType<typeof useOnboardingState>["steps"];
  completedSteps: number;
  totalSteps: number;
  progress: number;
  onAction: (step: OnboardingStep) => void;
  /** Show the title + count header row (default true). */
  showHeader?: boolean;
  /** Show the progress bar (default true). */
  showProgress?: boolean;
}

/**
 * Compact, always-expanded checklist used by both the desktop sidebar and the
 * mobile expandable pill. Each step is a single clickable row; the active step
 * is highlighted.
 */
function SidebarChecklist({
  steps,
  completedSteps,
  totalSteps,
  progress,
  onAction,
  showHeader = true,
  showProgress = true,
}: SidebarChecklistProps) {
  const { t } = useTranslation();

  return (
    <Box>
      {/* Header: title + count */}
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.75,
            px: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              color: SIDEBAR_PURPLE.textStrong,
            }}
          >
            {t("onboarding.checklist.title")}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "12px",
              fontWeight: 600,
              color: SIDEBAR_PURPLE.text,
              flexShrink: 0,
            }}
          >
            {completedSteps}/{totalSteps}
          </Typography>
        </Box>
      )}

      {/* Progress bar */}
      {showProgress && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            borderRadius: 2,
            mb: 0.75,
            mx: 0.5,
            backgroundColor: SIDEBAR_PURPLE.track,
            "& .MuiLinearProgress-bar": {
              backgroundColor: SIDEBAR_PURPLE.text,
              borderRadius: 2,
            },
          }}
        />
      )}

      {/* Step rows */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
        {ONBOARDING_STEPS.map((step) => {
          const stepState = steps.find((s) => s.id === step.id);
          return (
            <SidebarChecklistRow
              key={step.id}
              step={step}
              isCompleted={stepState?.completed ?? false}
              isActionable={stepState?.actionable ?? false}
              onAction={() => onAction(step)}
            />
          );
        })}
      </Box>
    </Box>
  );
}

interface SidebarChecklistRowProps {
  step: OnboardingStep;
  isCompleted: boolean;
  isActionable: boolean;
  onAction: () => void;
}

function SidebarChecklistRow({
  step,
  isCompleted,
  isActionable,
  onAction,
}: SidebarChecklistRowProps) {
  const { t } = useTranslation();
  const clickable = isActionable;

  const iconColor = isCompleted
    ? SIDEBAR_PURPLE.text
    : isActionable
    ? SIDEBAR_PURPLE.textStrong
    : SIDEBAR_PURPLE.muted;

  return (
    <Box
      onClick={clickable ? onAction : undefined}
      role={clickable ? "button" : undefined}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "6px",
        borderRadius: "8px",
        cursor: clickable ? "pointer" : "default",
        backgroundColor: isActionable ? SIDEBAR_PURPLE.activeBg : "transparent",
        transition: "background-color 0.15s",
        "&:hover": clickable
          ? { backgroundColor: SIDEBAR_PURPLE.activeBgHover }
          : undefined,
      }}
    >
      {isCompleted ? (
        <CheckCircleIcon
          sx={{ fontSize: 16, color: iconColor, flexShrink: 0 }}
        />
      ) : (
        <CheckCircleOutlineIcon
          sx={{ fontSize: 16, color: iconColor, flexShrink: 0 }}
        />
      )}
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontFamily: '"Inter", sans-serif',
          fontSize: "12.5px",
          lineHeight: 1.2,
          fontWeight: isActionable ? 600 : 500,
          color: isActionable
            ? SIDEBAR_PURPLE.textStrong
            : isCompleted
            ? SIDEBAR_PURPLE.muted
            : SIDEBAR_PURPLE.text,
          textDecoration: isCompleted ? "line-through" : "none",
          // Allow at most two lines, then ellipsis — never overflow the sidebar.
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {t(step.titleKey)}
      </Typography>
      {isActionable && (
        <KeyboardArrowRightIcon
          sx={{ fontSize: 18, color: SIDEBAR_PURPLE.text, flexShrink: 0 }}
        />
      )}
    </Box>
  );
}

// ============ MOBILE: PROGRESS PILL + BOTTOM SHEET ============

interface MobileOnboardingProps {
  steps: ReturnType<typeof useOnboardingState>["steps"];
  completedSteps: number;
  totalSteps: number;
  progress: number;
  onAction: (step: OnboardingStep) => void;
}

/**
 * Mobile onboarding presence: an unobtrusive progress pill pinned to the top
 * that expands in place into the same compact list used on desktop.
 */
function MobileOnboarding({
  steps,
  completedSteps,
  totalSteps,
  progress,
  onAction,
}: MobileOnboardingProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleRowAction = useCallback(
    (step: OnboardingStep) => {
      setOpen(false);
      onAction(step);
    },
    [onAction]
  );

  return (
    <>
      {/* Transparent backdrop to collapse when tapping outside (only when open) */}
      {open && (
        <Box
          onClick={() => setOpen(false)}
          sx={{ position: "fixed", inset: 0, zIndex: 1399 }}
        />
      )}

      {/* A single surface that morphs from a pill into a dropdown card */}
      <Box
        sx={{
          position: "fixed",
          top: "calc(env(safe-area-inset-top, 0px) + 10px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1400,
          width: open ? "min(360px, calc(100% - 24px))" : "fit-content",
          maxWidth: "calc(100% - 24px)",
          backgroundColor: "#ffffff",
          border: "1px solid #e0daee",
          borderRadius: open ? "14px" : "999px",
          boxShadow: "0 4px 14px rgba(67, 55, 101, 0.18)",
          overflow: "hidden",
          transition: "border-radius 0.15s",
        }}
      >
        {/* Pill / header — tap to toggle */}
        <Box
          onClick={() => setOpen((prev) => !prev)}
          role="button"
          aria-label={t("onboarding.checklist.title")}
          aria-expanded={open}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            pl: "10px",
            pr: "8px",
            py: "6px",
            cursor: "pointer",
            borderBottom: open ? "1px solid #ece8f5" : "none",
          }}
        >
          {/* Progress ring with track */}
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              width: 22,
              height: 22,
              flexShrink: 0,
            }}
          >
            <CircularProgress
              variant="determinate"
              value={100}
              size={22}
              thickness={4}
              sx={{ color: "#ece8f5" }}
            />
            <CircularProgress
              variant="determinate"
              value={progress}
              size={22}
              thickness={4}
              sx={{ color: SIDEBAR_PURPLE.text, position: "absolute", left: 0 }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              color: SIDEBAR_PURPLE.textStrong,
              whiteSpace: "nowrap",
            }}
          >
            {t("onboarding.checklist.title")}
          </Typography>
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <Box
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: "11px",
                fontWeight: 700,
                color: SIDEBAR_PURPLE.text,
                backgroundColor: "#f1eff7",
                borderRadius: "999px",
                px: "7px",
                py: "1px",
                flexShrink: 0,
              }}
            >
              {completedSteps}/{totalSteps}
            </Box>
            {open ? (
              <KeyboardArrowUpIcon
                sx={{ fontSize: 18, color: SIDEBAR_PURPLE.text, flexShrink: 0 }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{ fontSize: 18, color: SIDEBAR_PURPLE.text, flexShrink: 0 }}
              />
            )}
          </Box>
        </Box>

        {/* Expanded step list */}
        {open && (
          <Box sx={{ px: "10px", py: "8px" }}>
            <SidebarChecklist
              steps={steps}
              completedSteps={completedSteps}
              totalSteps={totalSteps}
              progress={progress}
              onAction={handleRowAction}
              showHeader={false}
              showProgress={false}
            />
          </Box>
        )}
      </Box>
    </>
  );
}

// ============ COMPLETION DIALOG ============

interface CompletionDialogProps {
  open: boolean;
  onClose: () => void;
}

function CompletionDialog({ open, onClose }: CompletionDialogProps) {
  const { t } = useTranslation();

  // Trigger confetti when dialog opens
  useEffect(() => {
    if (open) {
      analytics.track(AnalyticsEvents.ONBOARDING_COMPLETED);
      // Fire confetti from both sides
      const fireConfetti = () => {
        void confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.1, y: 0.6 },
        });
        void confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.9, y: 0.6 },
        });
      };
      // Small delay so the dialog is visible first
      const timer = setTimeout(fireConfetti, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "success.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <CelebrationIcon sx={{ fontSize: 40, color: "success.dark" }} />
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t("onboarding.completion.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t("onboarding.completion.message")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("onboarding.completion.helpText")}{" "}
          <MuiLink href="mailto:support@ruumi.io" underline="hover">
            {t("onboarding.completion.helpLink")}
          </MuiLink>
        </Typography>
        <Button variant="contained" size="large" onClick={onClose}>
          {t("onboarding.completion.button")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
